import React from "react";
import { IconButton, Tooltip, Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigationHistory } from "../../contexts/NavigationHistoryContext";

interface BackButtonProps {
  className?: string;
  showTooltip?: boolean;
  tooltipText?: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
}

const BackButton: React.FC<BackButtonProps> = ({
  className = "",
  showTooltip = true,
  tooltipText,
  disabled = false,
  size = "medium",
}) => {
  const { canGoBack, goBack, history } = useNavigationHistory();

  const isDisabled = disabled || !canGoBack;

  const handleClick = () => {
    if (!isDisabled) {
      goBack();
    }
  };

  // Generate tooltip text based on history
  const getTooltipText = () => {
    if (tooltipText) {
      return tooltipText;
    }

    if (!canGoBack) {
      return "No previous page available";
    }

    // Get the previous page path for tooltip
    const previousEntry = history[1];
    if (previousEntry) {
      const pathParts = previousEntry.path.split("/").filter(Boolean);
      const lastPart = pathParts[pathParts.length - 1] || "Home";
      return `Go back to ${lastPart}`;
    }

    return "Go back to previous page";
  };

  // Get icon size based on size prop
  const getIconSize = () => {
    switch (size) {
      case "small":
        return "16px";
      case "large":
        return "24px";
      default:
        return "20px";
    }
  };

  // Minimal icon-only button styles
  const getButtonStyles = () => {
    return {
      padding: 0,
      margin: 0,
      minWidth: "auto",
      width: "auto",
      height: "auto",
      border: "none",
      backgroundColor: "transparent",
      color: isDisabled ? "var(--light-grey)" : "var(--auto-btn-green)",
      cursor: isDisabled ? "not-allowed" : "pointer",
      transition: "color 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "transparent",
        color: isDisabled ? "var(--light-grey)" : "var(--auto-btn-green)",
      },
      "&:focus": {
        backgroundColor: "transparent",
      },
      "&.disabled": {
        pointerEvents: "none",
      },
      // Remove all MUI default button styling
      "&.MuiIconButton-root": {
        padding: 0,
        margin: 0,
        borderRadius: 0,
      },
    };
  };

  const backButton = (
    <IconButton
      onClick={handleClick}
      disabled={isDisabled}
      className={`back-button-minimal ${className} ${
        isDisabled ? "disabled" : ""
      }`}
      sx={getButtonStyles()}
      disableRipple
      disableFocusRipple
      aria-label={getTooltipText()}
      data-testid="back-button"
    >
      <ArrowBackIcon sx={{ fontSize: getIconSize() }} />
    </IconButton>
  );

  if (showTooltip) {
    return (
      <Tooltip title={getTooltipText()} placement="bottom" arrow>
        <Box component="span">{backButton}</Box>
      </Tooltip>
    );
  }

  return backButton;
};

export default BackButton;
